import {
  contactWhatsAppUrl,
  individualBookingUrl,
  legalBusiness,
  siteCanonicalUrl,
  youtubeChannelUrl,
} from '../siteDestinations'

/**
 * Ficha JSON-LD da home. É a fonte única: o pré-render injeta o resultado
 * no <head> do index.html, a landing renderiza as dúvidas a partir de
 * `faqItems` e os testes conferem que as duas coisas batem.
 */

export const instagramUrl = 'https://www.instagram.com/arthur.oficina.ia'

export const homeTitle = 'Curso de IA para iniciantes, online e prático | Oficina IA' // termo-ok: palavra buscada no Google (decisão de SEO)
export const homeDescription =
  'Curso de IA para iniciantes, online. Você me conta o que faz no computador e eu mostro onde a IA entra. A primeira conversa, de 30 minutos, é gratuita.' // termo-ok: palavra buscada no Google (decisão de SEO)

/** As dúvidas da landing, na ordem em que aparecem. A primeira é a pergunta que o Google busca. */
export const faqItems = [
  {
    question: 'Isso é um curso de IA para iniciantes?', // termo-ok: pergunta buscada no Google, respondida pelo que a Oficina é
    answer:
      'É um acompanhamento. A gente parte do que você já faz no computador e faz junto, em encontros online comigo, até você seguir sozinho. O primeiro encontro é uma conversa gratuita de 30 minutos.',
  },
  {
    question: 'Como funcionam os encontros?',
    answer:
      'Online, por videochamada, na sua tela, sobre as suas tarefas. São 9 horas de acompanhamento pago, de 1 a 2 horas por semana, conforme a sua agenda. A primeira conversa, gratuita, vem antes e não faz parte das 9 horas.',
  },
  {
    question: 'Preciso entender de tecnologia?',
    answer:
      'Você já entende do que faz no computador: o que precisa ficar pronto, para quem, até quando. A conversa começa aí. O resto eu mostro.',
  },
  {
    question: 'E se a IA inventar?',
    answer: 'Às vezes ela inventa. Por isso conferir é um passo seu: você abre o que saiu e compara com o que você sabe.',
  },
  {
    question: 'A IA faz tudo sozinha?',
    answer:
      'Ela faz o que você pede. Com você fica o que sempre foi seu: saber o que quer, dizer como tem que ficar e conferir se ficou.',
  },
  {
    question: 'E no trabalho, eu conto que uso IA?',
    answer:
      'Isso é decisão sua. O que a gente faz fica no seu computador, e você escolhe o que mostra e para quem. Se você lida com informações de clientes, esse cuidado entra logo no começo da primeira conversa: o que a IA vê e o que fica só com você.',
  },
] as const

const organizationId = `${siteCanonicalUrl}/#organization`
const personId = `${siteCanonicalUrl}/#arthur`
const websiteId = `${siteCanonicalUrl}/#website`
const webpageId = `${siteCanonicalUrl}/#webpage`
const logoUrl = `${siteCanonicalUrl}/assets/logo/oficina-ia-512.png`
const shareImageUrl = `${siteCanonicalUrl}/assets/share/oficina-ia-1200x630.png`

export const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: 'Oficina IA',
      legalName: legalBusiness.businessName,
      url: `${siteCanonicalUrl}/`,
      logo: { '@type': 'ImageObject', url: logoUrl, width: 512, height: 512 },
      image: shareImageUrl,
      email: legalBusiness.email,
      founder: { '@id': personId },
      areaServed: { '@type': 'Country', name: 'Brasil' },
      sameAs: [youtubeChannelUrl, instagramUrl],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        url: contactWhatsAppUrl,
        availableLanguage: 'Portuguese',
      },
    },
    {
      '@type': 'Person',
      '@id': personId,
      name: 'Arthur Fischer',
      url: `${siteCanonicalUrl}/#arthur`,
      image: `${siteCanonicalUrl}/assets/photography/arthur-corcovado.jpeg`,
      jobTitle: 'Professor',
      description:
        'Ensino idiomas para adultos há mais de dez anos. Criei a Oficina IA para ajudar pessoas de qualquer área a usar IA em tarefas reais e continuar por conta própria.',
      worksFor: { '@id': organizationId },
      sameAs: [youtubeChannelUrl, instagramUrl],
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      name: 'Oficina IA',
      url: `${siteCanonicalUrl}/`,
      inLanguage: 'pt-BR',
      publisher: { '@id': organizationId },
    },
    {
      '@type': 'WebPage',
      '@id': webpageId,
      url: `${siteCanonicalUrl}/`,
      name: homeTitle,
      description: homeDescription,
      inLanguage: 'pt-BR',
      isPartOf: { '@id': websiteId },
      about: { '@id': organizationId },
      primaryImageOfPage: shareImageUrl,
    },
    {
      '@type': 'Service',
      '@id': `${siteCanonicalUrl}/#individual`,
      name: 'Acompanhamento individual da Oficina IA',
      serviceType: 'Acompanhamento individual em uso de IA, online',
      description:
        'Orientação centrada na necessidade específica de uma pessoa, começando por uma primeira conversa gratuita de 30 minutos marcada na agenda online.',
      provider: { '@id': organizationId },
      areaServed: { '@type': 'Country', name: 'Brasil' },
      availableChannel: { '@type': 'ServiceChannel', serviceUrl: individualBookingUrl, name: 'Agenda online' },
    },
    {
      '@type': 'FAQPage',
      '@id': `${siteCanonicalUrl}/#faq`,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ],
} as const

export function structuredDataScript(): string {
  const json = JSON.stringify(structuredData).replace(/</g, '\\u003c')
  return `<script type="application/ld+json">${json}</script>`
}
